<template>
  <!-- A meter: monthly AI usage per plan, read from data/pricing-features.json (row
       aiTokenAllowance, plus aiDailyBrief), so it follows the pricing config. The bars fill and
       the multipliers count up once when the block scrolls into view; the server renders the
       final values. -->
  <section class="smt">
    <div class="smt-shell smt-grid">
      <header class="smt-heading">
        <h2>{{ chapter.title }}</h2>
        <p class="smt-intro">{{ chapter.intro }}</p>
        <NuxtLink :to="localePath('/pricing')" class="smt-link">
          {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
        </NuxtLink>
      </header>

      <div ref="rootEl" class="smt-card" :class="{ 'is-run': running || armed, 'is-armed': armed }">
        <div class="smt-card-head">
          <span>{{ usageRow.label }}</span>
          <span>{{ briefRow.label }}</span>
        </div>
        <ol class="smt-rows">
          <li v-for="(plan, i) in plans" :key="plan.slug" class="smt-row" :style="{ '--d': `${0.08 + i * 0.1}s` }">
            <span class="smt-plan">
              <strong translate="no">{{ plan.name }}</strong>
              <small>${{ plan.monthly }}/mo</small>
            </span>
            <span class="smt-bar">
              <i :style="{ '--w': `${plan.width}%` }" />
              <b>{{ plan.usage }}</b>
            </span>
            <span class="smt-brief" :class="{ 'is-yes': plan.brief }">
              <svg v-if="plan.brief" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
              <span v-else aria-hidden="true">–</span>
              <span class="sr-only">{{ plan.brief ? chapter.yes : chapter.no }}</span>
            </span>
          </li>
        </ol>
        <p class="smt-foot">{{ chapter.scaleNote }} {{ usageRow.tip }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import pricing from "~/data/pricing-features.json";

defineProps({
  chapter: { type: Object, required: true },
});

const { localePath } = useWorkspaceLocale();
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });

const rows = pricing.categories.flatMap((category) => category.rows);
const usageRow = rows.find((row) => row.key === "aiTokenAllowance");
const briefRow = rows.find((row) => row.key === "aiDailyBrief");
const valueText = (cell) => (cell.startsWith("v:") ? pricing.values[cell.slice(2)] ?? cell : cell);
const multiple = (cell) => Number.parseFloat(cell) || 1;
const top = Math.max(...usageRow.cells.map(multiple));

const plans = pricing.plans.map((plan, i) => ({
  slug: plan.slug,
  name: plan.name,
  monthly: plan.monthly,
  usage: valueText(usageRow.cells[i]),
  // Linear, with a floor so the smallest bar still reads as a bar.
  width: Math.max(6, (multiple(usageRow.cells[i]) / top) * 100),
  brief: briefRow.cells[i] === "yes",
}));
</script>

<style scoped>
.smt { background: #f8fafc; padding: 6.5rem 0; }
.dark .smt { background: #0b1220; }
.smt-shell { width: min(100% - 3rem, var(--story-max, 84rem)); margin: 0 auto; }
.smt-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .smt-grid { grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 4.5rem; } }
.smt-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .smt-heading h2 { color: #f8fafc; }
.smt-intro { margin-top: 1rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .smt-intro { color: #cbd5e1; }
.smt-link { display: inline-flex; gap: 0.4rem; margin-top: 1.25rem; color: #0e7490; font-weight: 800; }
.dark .smt-link { color: #67e8f9; }
.smt-link:hover { text-decoration: underline; }

.smt-card { border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; padding: 1.25rem 1.4rem; }
.dark .smt-card { border-color: #1e293b; background: #0f172a; }
.smt-card-head { display: flex; justify-content: space-between; gap: 1rem; color: #64748b; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; }
.dark .smt-card-head { color: #94a3b8; }
.smt-rows { display: grid; gap: 0.2rem; margin: 0.9rem 0 0; padding: 0; list-style: none; }
.smt-row { display: grid; grid-template-columns: 7.5rem 1fr 3rem; align-items: center; gap: 1rem; min-height: 3.1rem; }
.smt-row + .smt-row { border-top: 1px solid #f1f5f9; }
.dark .smt-row + .smt-row { border-top-color: #1e293b; }
.smt-plan { display: grid; line-height: 1.25; }
.smt-plan strong { color: #0f172a; font-size: 1.05rem; font-weight: 800; }
.dark .smt-plan strong { color: #f1f5f9; }
.smt-plan small { color: #64748b; font-size: 0.86rem; }
.dark .smt-plan small { color: #94a3b8; }
.smt-bar { position: relative; display: flex; align-items: center; height: 26px; }
.smt-bar i {
  display: block;
  width: var(--w);
  height: 100%;
  border-radius: 3px;
  background: #0e7490;
  transform-origin: left center;
}
.smt-bar b { margin-left: 0.6rem; color: #0f172a; font-size: 1rem; font-weight: 800; font-variant-numeric: tabular-nums; white-space: nowrap; }
.dark .smt-bar b { color: #f1f5f9; }
.dark .smt-bar i { background: #22b8d6; }
.smt-brief { display: grid; place-items: center; color: #94a3b8; }
.smt-brief.is-yes { color: #2d7a4e; }
.dark .smt-brief.is-yes { color: #6ed39a; }
.smt-brief svg { width: 18px; height: 18px; fill: currentColor; }
.smt-foot { margin-top: 1rem; color: #64748b; font-size: 0.82rem; line-height: 1.55; }
.dark .smt-foot { color: #94a3b8; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

.is-run .smt-bar i { animation: smt-fill 1s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .smt-bar b { animation: smt-fade 0.5s ease-out calc(var(--d) + 0.5s) both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes smt-fill { from { transform: scaleX(0); } to { transform: none; } }
@keyframes smt-fade { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: none; } }
@media (max-width: 639px) {
  .smt { padding: 4.5rem 0; }
  .smt-shell { width: min(100% - 2rem, var(--story-max, 84rem)); }
  .smt-card { padding: 1rem; }
  /* Phones: plan and briefing on one line, the bar full width under them. */
  .smt-row { grid-template-columns: 1fr auto; gap: 0.35rem 0.6rem; padding: 0.55rem 0; }
  .smt-bar { grid-column: 1 / -1; grid-row: 2; height: 20px; }
  .smt-plan { grid-template-columns: auto auto; justify-content: start; align-items: baseline; gap: 0.5rem; }
  .smt-card-head span:first-child { max-width: 60%; }
}
@media (prefers-reduced-motion: reduce) { .smt-card * { animation: none !important; } }
</style>
